import { useState, useMemo, useCallback, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import type { Payload } from "recharts/types/component/DefaultTooltipContent";

interface AllocationData {
  date: string;
  allocations: Record<string, number>;
}

interface Props {
  allocations: AllocationData[];
  isAdmin?: boolean;
}

export function HistoricalAllocations({ allocations, isAdmin: adminUser }: Props) {
  const [viewMode, setViewMode] = useState<"table" | "graph">("table");

  // Get unique years from data
  const years = useMemo(() => {
    const yearSet = new Set<number>();
    allocations.forEach((a) => yearSet.add(new Date(a.date).getFullYear()));
    return Array.from(yearSet).sort((a, b) => b - a); // Descending
  }, [allocations]);

  const [selectedYear, setSelectedYear] = useState<number | null>(
    years[0] || null,
  );

  // Reset selectedYear when allocations change (new backtest results)
  useEffect(() => {
    setSelectedYear(years[0] || null);
  }, [years]);

  // Filter allocations by selected year
  const filteredAllocations = useMemo(() => {
    if (!selectedYear) return allocations;
    return allocations.filter(
      (a) => new Date(a.date).getFullYear() === selectedYear,
    );
  }, [allocations, selectedYear]);

  // Get all unique tickers across all allocations
  const allTickers = useMemo(() => {
    const tickerSet = new Set<string>();
    filteredAllocations.forEach((a) => {
      Object.keys(a.allocations).forEach((ticker) => tickerSet.add(ticker));
    });
    return Array.from(tickerSet).sort();
  }, [filteredAllocations]);

  // Sort by date descending (most recent first)
  const sortedAllocations = useMemo(() => {
    return [...filteredAllocations].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [filteredAllocations]);

  // Detect trade days - when allocations differ from the previous day
  // A trade is detected when ANY ticker's allocation changes
  const tradeDays = useMemo(() => {
    const trades = new Set<string>();
    
    for (let i = 0; i < sortedAllocations.length - 1; i++) {
      const current = sortedAllocations[i];
      const previous = sortedAllocations[i + 1]; // Previous day (next in desc order)
      
      // Get all tickers from both days
      const allTickerKeys = new Set([
        ...Object.keys(current.allocations),
        ...Object.keys(previous.allocations)
      ]);
      
      let isTradeDay = false;
      
      for (const ticker of allTickerKeys) {
        const currentVal = current.allocations[ticker] || 0;
        const previousVal = previous.allocations[ticker] || 0;
        
        // Any change at all (round to avoid floating point issues)
        const currentRounded = Math.round(currentVal * 1000);
        const previousRounded = Math.round(previousVal * 1000);
        
        if (currentRounded !== previousRounded) {
          isTradeDay = true;
          break;
        }
      }
      
      if (isTradeDay) {
        trades.add(current.date);
      }
    }
    
    return trades;
  }, [sortedAllocations]);

  // Count actual trade days
  const tradeDayCount = tradeDays.size;

  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value < 0.001) return "—";
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatDate = (dateStr: string) => {
    // Parse as UTC to avoid timezone shift (ISO dates without time are UTC)
    const date = new Date(dateStr + "T00:00:00Z");
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  // Prepare data for stacked area chart
  const chartData = useMemo(() => {
    // Sort by date ascending for chart
    const sorted = [...filteredAllocations].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    return sorted.map((alloc) => {
      const row: Record<string, string | number> = { date: alloc.date };
      allTickers.forEach((ticker) => {
        row[ticker] = (alloc.allocations[ticker] || 0) * 100; // Convert to percentage
      });
      return row;
    });
  }, [filteredAllocations, allTickers]);

  // Color palette for tickers
  const tickerColors: Record<string, string> = {
    TQQQ: "#5a6df2", // primary blue
    TECL: "#10b981", // green
    SOXL: "#f59e0b", // amber
    UVXY: "#ef4444", // red
    SQQQ: "#8b5cf6", // purple
    DIA: "#06b6d4", // cyan
    XLP: "#ec4899", // pink
    SPY: "#6366f1", // indigo
    QQQ: "#14b8a6", // teal
  };

  const getTickerColor = (ticker: string, index: number) => {
    if (tickerColors[ticker]) return tickerColors[ticker];
    // Fallback colors
    const fallbackColors = [
      "#5a6df2",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#06b6d4",
      "#ec4899",
    ];
    return fallbackColors[index % fallbackColors.length];
  };

  const renderChartTooltip = useCallback(
    ({
      active,
      payload,
      label,
    }: {
      active?: boolean;
      payload?: readonly Payload<number, string>[];
      label?: string | number;
    }) => {
      if (active && payload && payload.length) {
        const date = new Date(String(label));
        const dateStr = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

        return (
          <div className="bg-surface-800 border border-surface-600 rounded-lg p-3 shadow-lg">
            <p className="text-surface-300 text-sm mb-2 font-medium">
              {dateStr}
            </p>
            {[...payload]
              .filter((p) => (p.value as number) > 0)
              .sort((a, b) => (b.value as number) - (a.value as number))
              .map((p) => (
                <div
                  key={String(p.dataKey)}
                  className="flex items-center gap-2 text-sm"
                >
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: p.fill as string }}
                  />
                  <span className="text-surface-300">{String(p.dataKey)}:</span>
                  <span className="text-surface-100 font-medium">
                    {(p.value as number).toFixed(1)}%
                  </span>
                </div>
              ))}
          </div>
        );
      }
      return null;
    },
    [],
  );

  return (
    <div className="glass-card p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-surface-100">
          Backtest Data
        </h3>
        <div className="flex items-center gap-4">
          {/* View Mode Toggle - only shown for admins who can switch to graph */}
          {adminUser && (
            <div className="flex bg-surface-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === "table"
                    ? "bg-surface-700 text-surface-100"
                    : "text-surface-400 hover:text-surface-200"
                }`}
              >
                Table
              </button>
              <button
                onClick={() => setViewMode("graph")}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === "graph"
                    ? "bg-surface-700 text-surface-100"
                    : "text-surface-400 hover:text-surface-200"
                }`}
              >
                Graph
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Year Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mb-1">
          {years.map((year) => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                selectedYear === year
                  ? "bg-primary-500 text-white"
                  : "bg-surface-800 text-surface-400 hover:bg-surface-700"
              }`}
            >
              {year}
            </button>
          ))}
        </div>
        <span className="text-surface-500 text-sm sm:ml-auto">
          {tradeDayCount} trades of {sortedAllocations.length} days
        </span>
      </div>

      {viewMode === "table" ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-700">
                <th className="text-left text-surface-400 font-medium py-3 px-2 sticky left-0 bg-surface-900">
                  Date
                </th>
                {allTickers.map((ticker) => (
                  <th
                    key={ticker}
                    className="text-center text-surface-400 font-medium py-3 px-2 min-w-[60px]"
                  >
                    {ticker}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedAllocations.slice(0, 50).map((alloc, idx) => {
                const isTradeDay = tradeDays.has(alloc.date);
                return (
                <tr
                  key={alloc.date}
                  className={`border-b border-surface-800 hover:bg-surface-800/50 ${
                    idx === 0 ? "bg-surface-800/30" : ""
                  } ${isTradeDay ? "bg-primary-500/5" : ""}`}
                >
                  <td className="py-2.5 px-2 text-surface-300 sticky left-0 bg-surface-900 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {isTradeDay ? (
                        <span className="w-5 h-5 flex items-center justify-center rounded bg-primary-500/20 text-primary-400" title="Trade executed">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
                            <path fillRule="evenodd" d="M13.78 10.47a.75.75 0 0 1 0 1.06l-2.25 2.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 1 1 1.06-1.06l.97.97V5.75a.75.75 0 0 1 1.5 0v5.69l.97-.97a.75.75 0 0 1 1.06 0ZM2.22 5.53a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1-1.06 1.06l-.97-.97v5.69a.75.75 0 0 1-1.5 0V4.56l-.97.97a.75.75 0 0 1-1.06 0Z" clipRule="evenodd" />
                          </svg>
                        </span>
                      ) : (
                        <span className="w-5" />
                      )}
                      {formatDate(alloc.date)}
                    </div>
                  </td>
                  {allTickers.map((ticker) => {
                    const weight = alloc.allocations[ticker];
                    const hasAllocation =
                      weight !== undefined && weight >= 0.001;
                    return (
                      <td
                        key={ticker}
                        className={`py-2.5 px-2 text-center ${
                          hasAllocation
                            ? "text-surface-200"
                            : "text-surface-600"
                        }`}
                      >
                        {formatPercent(weight)}
                      </td>
                    );
                  })}
                </tr>
              );
              })}
            </tbody>
          </table>
          {sortedAllocations.length > 50 && (
            <div className="text-center text-surface-500 text-sm py-4">
              Showing 50 of {sortedAllocations.length} days
            </div>
          )}
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} stackOffset="expand">
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis
                dataKey="date"
                stroke="#71717a"
                fontSize={12}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("en-US", {
                    month: "short",
                    year: "2-digit",
                  });
                }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#71717a"
                fontSize={12}
                tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                domain={[0, 1]}
              />
              <Tooltip content={renderChartTooltip} />
              <Legend
                wrapperStyle={{ paddingTop: 10 }}
                formatter={(value) => (
                  <span className="text-surface-300 text-sm">{value}</span>
                )}
              />
              {allTickers.map((ticker, index) => (
                <Area
                  key={ticker}
                  type="monotone"
                  dataKey={ticker}
                  stackId="1"
                  stroke={getTickerColor(ticker, index)}
                  fill={getTickerColor(ticker, index)}
                  fillOpacity={0.8}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
